import { DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import PhoneInput from "antd-phone-input"
import { Button } from "components/Button/Button"
import { Checkbox } from "components/Checkbox/Checkbox"
import { Divider } from "components/Divider/Divider"
import {
	Form,
	type FormListProps,
	type NamePath,
	type RuleRender,
} from "components/Form/Form"
import { Input, type InputRef } from "components/Input/Input"
import { type MenuItemType } from "components/Menu/Menu"
import { Select } from "components/Select/Select"
import { Switch } from "components/Switch/Switch"

import {
	AlternateContact,
	CRUDPermission,
	Relationship,
	RELATIONSHIPS,
} from "interface/user"
import { ReactElement, useMemo, useRef, useState } from "react"
import { cn } from "styles/cn"
import {
	isPhoneNumberUnique,
	normalizePhoneNumber,
	useCheckPhoneNumberAsync,
} from "./contact-info"

export function AlternateContactsFormItem({
	permission = "none",
	title = `Alternate contacts (${MAX_ACTIVE} max)`,
	formName,
	name = "alternateContacts",
	patientPhoneNumberPath,
	patientId,
	enrollmentId,
	...props
}: Partial<FormListProps> & {
	formName: string
	title?: string
	permission?: CRUDPermission
	patientPhoneNumberPath?: NamePath
	patientId?: string
	enrollmentId?: string
}) {
	const canEdit = permission === "edit"
	const isReadOnly = permission === "view"
	const hide = permission === "none"
	const { ruleCheckPhoneNumberAsync } = useCheckPhoneNumberAsync(
		patientId,
		enrollmentId,
	)
	const [customRelationships, setCustomRelationships] = useState<
		Relationship[]
	>([])
	const basePath = useMemo(() => (Array.isArray(name) ? name : [name]), [name])
	const alternateContacts = Form.useWatch<
		AlternateContact[] | null | undefined
	>(basePath, { preserve: true })
	const availableRelationships = useMemo<MenuItemType[]>(() => {
		const existingRelationships = (alternateContacts ?? [])
			.map((c) => c.relationship)
			.filter(Boolean)
		return Array.from(
			new Set([
				...RELATIONSHIPS,
				...existingRelationships,
				...customRelationships,
			]),
		)
			.sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))
			.map((relationship) => ({ key: relationship, label: relationship }))
	}, [alternateContacts, customRelationships])

	if (hide) {
		return null
	}

	return (
		<Form.List name={basePath} {...props}>
			{(fields, { add, remove }) => {
				const disableAdd =
					fields.filter((field) => {
						const contact = alternateContacts?.[field.name]
						return contact?.isActive === undefined || !!contact?.isActive
					}).length >= MAX_ACTIVE

				return (
					<div className="mb-6 flex flex-col gap-2">
						<div className="flex w-full items-center justify-between gap-2">
							<h3 className="text-lg font-semibold">
								{isReadOnly ? "Alternate contacts (read-only)" : title}
							</h3>
							<Button
								type="dashed"
								icon={<PlusOutlined aria-hidden />}
								className={cn("w-fit", isReadOnly && "invisible")}
								disabled={disableAdd}
								onClick={() => add({})}
							>
								Add
							</Button>
						</div>

						<ul
							aria-label={`${fields.length} alternate contacts available`}
							className={cn(
								"divide-y divide-dashed divide-grey-20 rounded-lg border border-grey-20 p-2",
								fields.length === 0 && "hidden",
							)}
						>
							{fields.map((field, index) => {
								const contact = alternateContacts?.[field.name]
								const isEditing = !!contact?._id
								const disabled =
									contact?.isActive !== undefined && !contact?.isActive
								const fieldValuePath = [field.name, "phoneNumber", "value"]
								const htmlFor = `${formName}_${basePath.join("_")}_${fieldValuePath.join("_")}`
								const dependenciesPaths = fields
									.filter((otherField) => otherField.name !== field.name)
									.map((otherField) => [
										...basePath,
										otherField.name,
										"phoneNumber",
										"value",
									])
								const fieldDependencies = [
									...dependenciesPaths,
									patientPhoneNumberPath,
								]
								const ruleUniquePhoneNumber: RuleRender = ({
									getFieldValue,
								}) => ({
									message:
										"This phone number is already associated with this patient",
									validator: function () {
										const otherPhoneNumbers = dependenciesPaths.map((path) =>
											getFieldValue(path),
										)
										if (patientPhoneNumberPath) {
											const patientPhoneNumberValue = getFieldValue(
												patientPhoneNumberPath,
											)
											if (patientPhoneNumberValue) {
												otherPhoneNumbers.push(patientPhoneNumberValue)
											}
										}
										const fieldValue = getFieldValue([
											...basePath,
											...fieldValuePath,
										])

										return isPhoneNumberUnique(otherPhoneNumbers, fieldValue)()
									},
								})

								return (
									<li
										key={field.key}
										className="not-first:pt-2 not-last:pb-3"
										aria-label={`${contact?.relationship ?? `#${index + 1}`} contact`}
										aria-readonly={isReadOnly}
									>
										<div className="flex gap-2">
											<Form.Item
												name={[field.name, "fullName"]}
												label="Name"
												className="w-full"
												rules={[
													{ required: true, whitespace: true },
													{
														pattern: /^[A-Za-z\s.'-]+$/,
														message:
															"Please enter only letters, spaces, hyphens, and dots",
													},
													{
														max: 100,
														message: "Full name cannot exceed 100 characters",
													},
												]}
											>
												<Input disabled={disabled} readOnly={isReadOnly} />
											</Form.Item>
											<Form.Item
												name={[field.name, "relationship"]}
												label="Relationship"
												className="min-w-32"
												rules={[{ required: true }]}
											>
												<Select
													disabled={disabled}
													aria-readonly={isReadOnly}
													className={cn(isReadOnly && "pointer-events-none")}
													popupMatchSelectWidth={false}
													options={availableRelationships.map(
														({ key: value, label }) => ({
															value,
															label,
														}),
													)}
													popupRender={(menu) => (
														<RelationshipMenu
															menu={menu}
															onAdd={(customRelationship) => {
																// only add if it's a new relationship
																if (
																	!availableRelationships.some(
																		({ label }) => label === customRelationship,
																	)
																) {
																	setCustomRelationships((prev) => [
																		...prev,
																		customRelationship,
																	])
																}
															}}
														/>
													)}
												/>
											</Form.Item>
										</div>

										<Form.Item required label="Phone number" htmlFor={htmlFor}>
											<div className="flex flex-col gap-1">
												<Form.Item
													noStyle
													name={fieldValuePath}
													dependencies={fieldDependencies}
													normalize={normalizePhoneNumber}
													validateFirst
													validateDebounce={300}
													validateTrigger="onBlur"
													rules={[
														{
															required: true,
															message: "The phone number is empty",
														},
														ruleUniquePhoneNumber,
														ruleCheckPhoneNumberAsync,
													]}
												>
													<PhoneInput
														disabled={disabled}
														readOnly={isReadOnly}
													/>
												</Form.Item>
												<Form.Item
													noStyle
													name={[
														field.name,
														"phoneNumber",
														"supportsTextMessages",
													]}
													valuePropName="checked"
												>
													<Checkbox
														disabled={disabled}
														aria-readonly={isReadOnly}
														className={cn(isReadOnly && "pointer-events-none")}
													>
														Supports text messages
													</Checkbox>
												</Form.Item>
											</div>
										</Form.Item>

										{isEditing && canEdit && contact.hasCommunications && (
											<Form.Item
												label="Archived?"
												name={[field.name, "isActive"]}
												valuePropName="checked"
												colon={false}
												layout="horizontal"
												className="flex justify-end"
												getValueProps={(value) => ({ checked: !value })}
												getValueFromEvent={(checked) => !checked}
											>
												<Switch
													aria-readonly={isReadOnly}
													className={cn(isReadOnly && "pointer-events-none")}
												/>
											</Form.Item>
										)}

										{!contact?.hasCommunications && canEdit && (
											<div className="flex justify-end">
												<Button
													danger
													icon={<DeleteOutlined aria-hidden />}
													onClick={() => remove(index)}
												>
													Remove
												</Button>
											</div>
										)}
									</li>
								)
							})}
						</ul>
					</div>
				)
			}}
		</Form.List>
	)
}

function RelationshipMenu({
	menu,
	onAdd,
}: {
	menu: ReactElement
	onAdd: (relationship: Relationship) => void
}) {
	const inputRef = useRef<InputRef>(null)
	const [relationship, setRelationship] = useState("")
	return (
		<div role="menu" aria-label="Relationship menu">
			{menu}
			<Divider className="my-1" />
			<div className="flex gap-1 p-1">
				<Input
					ref={inputRef}
					value={relationship}
					aria-label="Custom relationship name"
					onChange={(e) => setRelationship(e.currentTarget.value)}
					onKeyDown={(e) => e.stopPropagation()}
				/>
				<Button
					type="text"
					icon={<PlusOutlined aria-hidden />}
					disabled={relationship.trim().length === 0}
					onMouseDown={(e) => e.preventDefault()}
					onClick={() => {
						onAdd(relationship)
						setRelationship("")
					}}
				>
					Add
				</Button>
			</div>
		</div>
	)
}

const MAX_ACTIVE = 3
